import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Rocket, Brain, Cpu, Users, Award,
  CheckCircle, Target, TrendingUp, Monitor,
  Server, GraduationCap, UserCheck
} from "lucide-react";
import { SEO } from "@/components/shared";
import { fadeInUp, staggerContainer, viewportOnce, HERO_IMAGES } from "@/constants";
import { PHOTOS, PhotoBand, CardImage, FramedPhoto } from "@/components/Photo";

const UNNATI_IMAGES = [PHOTOS.labTeacher, PHOTOS.studentsLaptop, PHOTOS.lecture];

const PROGRAM_HIGHLIGHTS = [
  { id: 'ph-1', text: "Customized to industry context" },
  { id: 'ph-2', text: "Interactive workshops & role-plays" },
  { id: 'ph-3', text: "Real-world case studies" },
  { id: 'ph-4', text: "Post-training support & coaching" },
  { id: 'ph-5', text: "Certification upon completion" },
];

const AI_COURSES = [
  { id: 'course-ai', title: "AI Fundamentals", desc: "Core concepts and applications" },
  { id: 'course-ml', title: "Machine Learning Essentials", desc: "Practical ML implementation" },
  { id: 'course-gen', title: "Generative AI", desc: "Latest in Gen AI technologies" },
  { id: 'course-biz', title: "AI for Business", desc: "Strategic AI adoption" },
  { id: 'course-proj', title: "Hands-on Projects", desc: "Real-world AI applications" },
];

const UNNATI_CARDS = [
  { id: 'unnati-labs', icon: Server, title: "AI Labs Setup", desc: "State-of-the-art Intel\u00AE Unnati AI Labs with cutting-edge hardware and software resources for hands-on learning." },
  { id: 'unnati-training', icon: GraduationCap, title: "Industrial Training", desc: "Industry-relevant certifications aligned with AICTE recommendations, guided by Intel and industry mentors." },
  { id: 'unnati-faculty', icon: UserCheck, title: "Faculty Development", desc: "Comprehensive training programs for educators to effectively teach AI concepts and applications." },
];

const SKILLS = [
  { id: 'skill-prog', name: "Programming" },
  { id: 'skill-data', name: "Data Science" },
  { id: 'skill-cv', name: "Computer Vision" },
  { id: 'skill-nlp', name: "NLP & Gen AI" },
  { id: 'skill-ethics', name: "AI Ethics" },
  { id: 'skill-critical', name: "Critical Thinking" },
  { id: 'skill-problem', name: "Problem Solving" },
  { id: 'skill-collab', name: "Collaboration" },
];

export default function ProgramsPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [hash]);

  return (
    <div className="pt-24">
      <SEO 
        title="Programs"
        description="Explore Qbic Catalyst training programs: Sales Transformation, AI Catalyst Training, Intel Unnati Partnership, and Intel AI for Future Workforce. Industry-centric curriculum delivered by experts."
        keywords="sales training, AI training programs, Intel Unnati, AI for Future Workforce, workforce training, professional development, AI curriculum, sales transformation"
        path="/programs"
      />
      {/* Hero */}
      <section className="section page-hero" style={{ "--hero-img": `url(${HERO_IMAGES.programs})` }}>
        <div className="container-custom">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-4xl"
          >
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
              Our Programs
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">
              Transform Your Workforce with{' '}
              <span className="text-gradient">World-Class Training</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-[#A0ABC0]">
              Industry-centric curriculum delivered by passionate experts, available online and offline with customization support.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Sales Transformation */}
      <section id="sales-transformation" className="section theme-light tint-ivory scroll-mt-24">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="flex items-center gap-3 mb-6">
                <Rocket className="text-[#C9A962]" size={24} />
                <span className="text-[#C9A962] text-sm uppercase tracking-[0.2em]">Sales Transformation</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Explore Beyond Your Boundaries&#8482;
              </h2>
              <p className="text-[#A0ABC0] text-lg mb-6">
                Our flagship sales enablement program designed to transform sales professionals into high-performing consultants who drive exceptional results.
              </p>
              <ul className="feature-list text-[#A0ABC0] mb-8">
                <li>Advanced consultative selling techniques</li>
                <li>Strategic account management frameworks</li>
                <li>Negotiation and closing excellence</li>
                <li>Building lasting client relationships</li>
                <li>Personal branding and professional presence</li>
              </ul>
              <div className="flex items-center gap-4 p-4 bg-[#0A1628] rounded-lg border border-[#2A3648]">
                <Award className="text-[#C9A962]" size={32} />
                <div>
                  <p className="font-semibold">1500+ Professionals Trained</p>
                  <p className="text-[#A0ABC0] text-sm">Across leading enterprises</p>
                </div>
              </div>
            </motion.div>
            <FramedPhoto src={PHOTOS.sales} alt="Sales team in a training session" testId="programs-sales-photo" />
          </motion.div>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="mt-16"
            data-testid="programs-sales-highlights"
          >
            <motion.h4 variants={fadeInUp} className="font-bold mb-5">Program Highlights</motion.h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {PROGRAM_HIGHLIGHTS.map((item) => (
                <motion.div key={item.id} variants={fadeInUp} className="flex items-start gap-3 p-5 bg-[#0A1628] rounded-lg border border-[#2A3648] card-hover">
                  <CheckCircle className="text-[#C9A962] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-[#A0ABC0] text-sm">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Catalyst */}
      <section id="ai-catalyst" className="section bg-[#0A1628] scroll-mt-24">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <FramedPhoto src={PHOTOS.genai} alt="Artificial intelligence concept" className="order-2 lg:order-1" testId="programs-ai-photo" />
            <motion.div variants={fadeInUp} className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <Brain className="text-[#C9A962]" size={24} />
                <span className="text-[#C9A962] text-sm uppercase tracking-[0.2em]">AI Training</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                AI Catalyst&#8482; Training Programs
              </h2>
              <p className="text-[#A0ABC0] text-lg mb-6">
                Demystifying AI through comprehensive, beginner-to-advanced programs designed for practical learning. Our industry-centric curriculum is delivered by passionate experts with real-world experience.
              </p>
              <ul className="feature-list text-[#A0ABC0] mb-8">
                <li>From fundamentals to advanced applications</li>
                <li>Hands-on, project-based learning</li>
                <li>Industry-specific use cases</li>
                <li>Online and offline delivery options</li>
                <li>Customization for target audience</li>
              </ul>
            </motion.div>
          </motion.div>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="mt-16"
            data-testid="programs-ai-courses"
          >
            <motion.h4 variants={fadeInUp} className="font-bold mb-5">Course Offerings</motion.h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {AI_COURSES.map((item, i) => (
                <motion.div key={item.id} variants={fadeInUp} className="flex items-start gap-4 p-5 bg-[#121E30] rounded-lg border border-[#2A3648] card-hover">
                  <span className="font-mono text-xs text-[#C9A962] mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-semibold text-sm mb-1">{item.title}</p>
                    <p className="text-[#A0ABC0] text-xs">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intel Unnati Partnership */}
      <section id="intel-unnati" className="section theme-light tint-sky scroll-mt-24">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="text-[#C9A962]" size={24} />
                <span className="text-[#C9A962] text-sm uppercase tracking-[0.2em]">Strategic Partnership</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Intel&#174; Unnati Partnership
              </h2>
              <p className="text-[#A0ABC0] text-lg max-w-3xl">
                Qbic Catalyst Partners collaborates with Intel's Unnati program to bring world-class AI education and infrastructure to institutions across India. Together, we're building an AI-ready future.
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              {UNNATI_CARDS.map((item, i) => (
                <motion.div
                  key={item.id}
                  variants={fadeInUp}
                  className="group overflow-hidden p-6 md:p-8 bg-[#0A1628] rounded-lg border border-[#2A3648] card-hover flex flex-col h-full"
                >
                  <CardImage src={UNNATI_IMAGES[i]} alt={item.title} pad="p-6 md:p-8" />
                  <div className="icon-container mb-4 w-12 h-12">
                    <item.icon className="text-[#C9A962]" size={22} />
                  </div>
                  <h4 className="font-semibold text-lg mb-3">{item.title}</h4>
                  <p className="text-[#A0ABC0] text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-12 p-8 bg-[#0A1628] rounded-lg border border-[#2A3648]">
              <h4 className="font-bold mb-4">Qbic Catalyst Value Addition</h4>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  "Customized curriculum development aligned with institutional goals",
                  "Expert facilitation and ongoing technical support",
                  "Industry connect programs and placement assistance",
                  "Research collaboration and project mentorship",
                ].map((text, i) => (
                  <div key={`val-${i}`} className="flex items-start gap-3">
                    <CheckCircle className="text-[#C9A962] flex-shrink-0 mt-1" size={18} />
                    <p className="text-[#A0ABC0]">{text}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Webinar CTA */}
            <motion.div variants={fadeInUp} className="mt-8 p-6 bg-gradient-to-r from-[#C9A962]/10 to-[#C9A962]/5 border border-[#C9A962]/30 rounded-lg flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C9A962]/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <Monitor className="text-[#C9A962]" size={22} />
                </div>
                <div>
                  <p className="font-semibold text-white">Upcoming Webinar: Intel&#174; Unnati Program Briefing</p>
                  <p className="text-[#A0ABC0] text-sm">9th May 2026, 11:00 AM IST &#8212; Learn how your institution can benefit</p>
                </div>
              </div>
              <Link to="/webinar/intel-unnati" className="btn-primary text-sm flex items-center gap-2 whitespace-nowrap" data-testid="unnati-webinar-cta">
                Register Now <ArrowRight size={16} />
              </Link>
            </motion.div>

            {/* AI CoE CTA */}
            <motion.div variants={fadeInUp} className="mt-4 p-6 bg-[#0A1628] border border-[#2A3648] rounded-lg flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C9A962]/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <Cpu className="text-[#C9A962]" size={22} />
                </div>
                <div>
                  <p className="font-semibold text-white">Set Up an AI Centre of Excellence</p>
                  <p className="text-[#A0ABC0] text-sm">Explore lab tracks, architecture, rollout and benefits &#8212; and request a tailored proposal</p>
                </div>
              </div>
              <Link to="/ai-centre-of-excellence" className="btn-primary text-sm flex items-center gap-2 whitespace-nowrap" data-testid="unnati-coe-cta">
                Explore AI CoE <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Intel AI for Future Workforce */}
      <section id="future-workforce" className="section bg-[#0A1628] scroll-mt-24">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Users className="text-[#C9A962]" size={24} />
                <span className="text-[#C9A962] text-sm uppercase tracking-[0.2em]">Workforce Development</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Intel&#174; AI For Future Workforce
              </h2>
              <p className="text-[#A0ABC0] text-lg max-w-3xl">
                Empowering the next generation of workers with essential AI skills for employability in the digital economy. This program prepares students aged 16+ from vocational and community institutions.
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8 mb-12"
            >
              <motion.div variants={fadeInUp} className="p-8 bg-[#121E30] rounded-lg border border-[#2A3648]">
                <h4 className="font-bold mb-6">Program Objectives</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Target className="text-[#C9A962] flex-shrink-0 mt-1" size={18} />
                    <span className="text-[#A0ABC0]">Gain AI technical confidence and foundational skills</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <TrendingUp className="text-[#C9A962] flex-shrink-0 mt-1" size={18} />
                    <span className="text-[#A0ABC0]">Enhance employability for AI-related positions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Award className="text-[#C9A962] flex-shrink-0 mt-1" size={18} />
                    <span className="text-[#A0ABC0]">Produce evidence of competency for employment</span>
                  </li>
                </ul>
              </motion.div>
              <motion.div variants={fadeInUp} className="p-8 bg-[#121E30] rounded-lg border border-[#2A3648]">
                <h4 className="font-bold mb-6">Skills Gained</h4>
                <div className="grid grid-cols-2 gap-3">
                  {SKILLS.map((skill) => (
                    <div key={skill.id} className="flex items-center gap-2 text-[#A0ABC0] text-sm">
                      <CheckCircle className="text-[#C9A962]" size={14} />
                      <span>{skill.name}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PhotoBand
        src={PHOTOS.graduation}
        eyebrow="Outcomes"
        title="Building an AI-ready workforce, one cohort at a time"
        text="1500+ professionals trained and a growing community of certified students and faculty."
        testId="programs-band"
      />

      {/* CTA */}
      <section className="section theme-light tint-mist">
        <div className="container-custom text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="max-w-2xl mx-auto"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Team?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] text-lg mb-8">
              Contact us to explore customized training programs tailored to your organization's needs.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="programs-cta">
                Discuss Training Needs <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
